import { AdditiveBlending, Color, ColorRepresentation, DoubleSide, Group, Mesh, PlaneGeometry, ShaderMaterial } from 'three';

const DEFAULT_COLOR = 0x33aaff;
const DEFAULT_RADIUS = 0.029;
const DEFAULT_LENGTH = 0.01;
const DEFAULT_HEIGHT = 0;
const DEFAULT_SPACING = 0.08;
const DEFAULT_GLOW = 1.2;
const DEFAULT_VIBRATION = 0;
const PLANE_SIZE = 0.2;
const BLINK_DURATION = 0.15;
const BLINK_MIN_INTERVAL = 1.5;
const BLINK_MAX_INTERVAL = 5.5;

export interface EyesOptions {
    color?: ColorRepresentation;
    radius?: number;
    length?: number;
    height?: number;
    spacing?: number;
    glow?: number;
    vibration?: number;
}

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uGlow;
uniform float uTime;
uniform float uVibration;
uniform float uThickness;
uniform float uLength;
uniform float uHeight;
uniform float uPlaneWidth;
uniform float uPlaneHeight;

varying vec2 vUv;

void main() {
    float px = (vUv.x - 0.5) * uPlaneWidth;
    float py = (vUv.y - 0.5) * uPlaneHeight;
    float normX = (vUv.x - 0.5) * 2.0;

    float envelope = 1.0 - (normX * normX);
    float wave = sin(normX * 15.0 - uTime * 20.0) * 0.5
               + cos(normX * 25.0 + uTime * 35.0) * 0.5;
    py += wave * uVibration * envelope;

    float dX = max(0.0, abs(px) - uLength);
    float dY = max(0.0, abs(py) - uHeight);
    float dist = length(vec2(dX, dY));

    float aa = 0.003;
    float core = 1.0 - smoothstep(uThickness - aa, uThickness, dist);

    float glowThickness = uThickness + uGlow * 0.03;
    float glow = 1.0 - smoothstep(0.0, glowThickness, dist);
    glow = pow(glow, 2.0);

    float alpha = core + glow * 0.6;
    vec3 finalColor = uColor * (1.0 + core * 0.5);

    gl_FragColor = vec4(finalColor, clamp(alpha, 0.0, 1.0));
}
`;

export class Eyes extends Group {
    private leftEye: Mesh;
    private rightEye: Mesh;
    private eyeMat: ShaderMaterial;
    private spacing: number;
    private blinkTimer = 0;
    private isBlinking = false;
    private nextBlink: number;

    constructor(opts: EyesOptions = {}) {
        super();

        const color = new Color(opts.color ?? DEFAULT_COLOR);
        const radius = opts.radius ?? DEFAULT_RADIUS;
        const length = opts.length ?? DEFAULT_LENGTH;
        const height = opts.height ?? DEFAULT_HEIGHT;
        this.spacing = opts.spacing ?? DEFAULT_SPACING;
        const glow = opts.glow ?? DEFAULT_GLOW;
        const vibration = opts.vibration ?? DEFAULT_VIBRATION;

        const geo = new PlaneGeometry(PLANE_SIZE, PLANE_SIZE, 1, 1);

        this.eyeMat = new ShaderMaterial({
            uniforms: {
                uColor: { value: color },
                uGlow: { value: glow },
                uThickness: { value: radius },
                uLength: { value: length / 2.0 },
                uHeight: { value: height / 2.0 },
                uPlaneWidth: { value: PLANE_SIZE },
                uPlaneHeight: { value: PLANE_SIZE },
                uTime: { value: 0 },
                uVibration: { value: vibration },
            },
            vertexShader: VERT,
            fragmentShader: FRAG,
            transparent: true,
            depthWrite: false,
            side: DoubleSide,
            blending: AdditiveBlending,
        });

        this.leftEye = new Mesh(geo, this.eyeMat);
        this.rightEye = new Mesh(geo, this.eyeMat);
        this.leftEye.renderOrder = 100;
        this.rightEye.renderOrder = 100;
        this.add(this.leftEye);
        this.add(this.rightEye);

        this.updateSpacing();
        this.nextBlink = BLINK_MIN_INTERVAL + Math.random() * (BLINK_MAX_INTERVAL - BLINK_MIN_INTERVAL);
    }

    private updateSpacing(): void {
        this.leftEye.position.set(-this.spacing, 0, 0);
        this.rightEye.position.set(this.spacing, 0, 0);
    }

    update(dt: number): void {
        this.eyeMat.uniforms.uTime.value += dt;
        this.blinkTimer += dt;

        if (this.isBlinking) {
            const progress = this.blinkTimer / BLINK_DURATION;
            if (progress >= 1.0) {
                this.isBlinking = false;
                this.blinkTimer = 0;
                this.nextBlink = BLINK_MIN_INTERVAL + Math.random() * (BLINK_MAX_INTERVAL - BLINK_MIN_INTERVAL);
                this.leftEye.scale.y = 1;
                this.rightEye.scale.y = 1;
            } else {
                const scaleY = Math.max(0.01, 4.0 * Math.pow(progress - 0.5, 2));
                this.leftEye.scale.y = scaleY;
                this.rightEye.scale.y = scaleY;
            }
        } else if (this.blinkTimer >= this.nextBlink) {
            this.isBlinking = true;
            this.blinkTimer = 0;
        }
    }

    dispose(): void {
        this.leftEye.geometry.dispose();
        this.eyeMat.dispose();
    }
}
